/* pitchRunner — where NoVA's pitch IS, in this browser.

   The tree and the traversal live in data/pitch.js (a generated copy of
   backend/shared/pitch.js); this module keeps the one piece of state the
   tools need — the current node, tier and the visited trail — at module
   level so it survives the surface swap when a pitch crosses between the
   public dock (AgentDock) and an organization console (CopilotPanel), and
   so the text-chat fallback can send it along to /voice/chat.

   Both surfaces call handlePitchTool() from their client-tool handler and
   applyPitchStep() for a `pitch_go` chat action; the stage rail reads the
   state through usePitchState(). [[nova-pitch]] */
import { useSyncExternalStore } from 'react';
import { PITCH, pitchStart, pitchStep, pitchIndex } from '../data/pitch.js';

const PITCH_TOOLS = new Set(['pitch_start', 'pitch_step']);
export function isPitchTool(name) { return PITCH_TOOLS.has(name); }

let state = null; // { active, focus, nodeId, tier, visited } | null
const listeners = new Set();
function setState(next) {
  state = next;
  listeners.forEach((fn) => { try { fn(); } catch (_) { /* a listener threw */ } });
  try { window.dispatchEvent(new CustomEvent('nv:pitch', { detail: { state } })); } catch (_) { /* no window */ }
}

export function pitchState() { return state; }
export function endPitch() { if (state) setState({ ...state, active: false }); }
export function resetPitch() { setState(null); }

function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function usePitchState() { return useSyncExternalStore(subscribe, pitchState, pitchState); }

// Move the browser to a step's screen: a new route (then the section once
// it mounts — scrollPage polls for the id), or a glide on the same page.
// `whenAudible` (optional) delays the move until the sentence that caused
// it is being heard, like navigate_app in the dock.
function showStep(step, { navigate, scrollPage, whenAudible } = {}) {
  const node = step?.node;
  if (!node?.route) return;
  const here = `${window.location.pathname}`;
  const move = () => {
    if (here !== node.route) {
      if (navigate) navigate(node.route);
      if (node.section && scrollPage) scrollPage(node.section);
      else if (!node.section) window.scrollTo({ top: 0 });
    } else if (scrollPage) {
      scrollPage(node.section || 'top');
    }
  };
  if (whenAudible) whenAudible(move); else move();
}

// Store a step (from a tool here, or a `pitch_go` action from /voice/chat),
// show its screen, and hand the payload back for the model.
export function applyPitchStep(step, helpers = {}) {
  if (!step || step.error) return step || { error: 'no step' };
  if (step.stopped) {
    setState(step.state ? { ...step.state, active: false } : null);
    return step;
  }
  if (step.state) setState({ ...step.state, active: true });
  showStep(step, helpers);
  return step;
}

export function handlePitchTool(name, args = {}, helpers = {}) {
  // helpers.member: a signed-in person may reach the members-only beats (the books on /docs).
  const opts = { gated: !!helpers.member };
  if (name === 'pitch_start') return applyPitchStep(pitchStart(args?.focus, opts), helpers);
  if (name === 'pitch_step') return applyPitchStep(pitchStep(state, args?.to, opts), helpers);
  return undefined;
}

// The chapter strip for the stage rail: the top-level nodes with a status,
// the current node's path and the "n of N" line.
export function pitchOutline(s = state) {
  if (!s?.active || !s.nodeId) return null;
  const { byId } = pitchIndex();
  const cur = byId.get(s.nodeId);
  if (!cur) return null;
  let topId = s.nodeId;
  while (byId.get(topId)?.parent) topId = byId.get(topId).parent;
  const visited = new Set(s.visited || []);
  const topIdx = PITCH.findIndex((n) => n.id === topId);
  const chapters = PITCH.map((n, i) => ({
    id: n.id,
    title: n.title,
    status: n.id === topId ? 'current' : i < topIdx || visited.has(n.id) ? 'done' : 'upcoming',
  }));
  const path = [];
  for (let id = s.nodeId; id; id = byId.get(id)?.parent) path.unshift(byId.get(id).node.title);
  return { chapters, path, current: { id: s.nodeId, title: cur.node.title }, position: `${topIdx + 1} of ${PITCH.length}` };
}
